/**
 * O checklist de uma tarefa: barra de progresso, itens, responsável por item e o campo
 * de adicionar.
 *
 * <p>Mora fora do modal porque agora tem <b>dois</b> modais: a frente do roadmap e o card
 * do Dev Board. Os dois têm cabeçalhos diferentes — um tem bloco e janela de meses, o
 * outro tem coluna — mas o checklist é o mesmo, nas mesmas rotas. Duplicá-lo garantiria
 * que um dia só um dos dois ganhasse uma correção.
 *
 * <p>Ele fala direto com a API pelo {@code tarefaId}: quem o usa não passa dados nem
 * recebe eventos de volta.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../api/cliente';
import type { Membro } from '../api/tipos';
import { chaveChecklist, useMutacoesChecklist } from './roadmapApi';
import type { Checklist, ItemChecklist } from './roadmapApi';
import { Avatar } from '../comum/Avatar';

export function ChecklistDaTarefa({
  tarefaId,
  membros,
}: {
  tarefaId: number;
  membros: Membro[];
}) {
  const { criarItem, editarItem, excluirItem } = useMutacoesChecklist(tarefaId);
  const [novoItem, setNovoItem] = useState('');
  const [editando, setEditando] = useState<number | null>(null);
  const [picker, setPicker] = useState<{ item: ItemChecklist; x: number; y: number } | null>(null);

  const { data: checklist } = useQuery<Checklist>({
    queryKey: chaveChecklist(tarefaId),
    queryFn: () => api(`/api/tarefas/${tarefaId}/checklist`),
  });

  const itens = checklist?.itens ?? [];
  const feitos = itens.filter((i) => i.feito).length;

  function adicionarItem() {
    const texto = novoItem.trim();
    if (!texto) return;
    criarItem.mutate(texto, { onSuccess: () => setNovoItem('') });
  }

  return (
    <>
      <div className="mprog">
        <div className="track">
          <div
            className="fill"
            style={{ width: itens.length ? `${(feitos / itens.length) * 100}%` : 0 }}
          />
        </div>
        <div className="n">
          {feitos} / {itens.length}
        </div>
      </div>

      <div className="mbody">
        {itens.length === 0 ? (
          <div className="mvazio">
            <b>Nenhum item ainda</b>
            Quebre isto em tarefas menores.
            <br />
            Ex.: "Fechar a lista de regiões", "Validar com o Hiro".
          </div>
        ) : (
          itens.map((item) => (
            <div key={item.id} className={`mitem${item.feito ? ' done' : ''}`}>
              <button
                className={`cbox${item.feito ? ' on' : ''}`}
                onClick={() => editarItem.mutate({ id: item.id, feito: !item.feito })}
              >
                <svg viewBox="0 0 12 12">
                  <polyline points="2,6.4 4.6,9 10,3" />
                </svg>
              </button>

              {editando === item.id ? (
                <EditorDeTexto
                  valorInicial={item.texto}
                  aoConfirmar={(texto) => {
                    if (texto && texto !== item.texto) {
                      editarItem.mutate({ id: item.id, texto });
                    }
                    setEditando(null);
                  }}
                />
              ) : (
                <span className="txt" onClick={() => setEditando(item.id)}>
                  {item.texto}
                </span>
              )}

              <button
                className={`iresp${item.responsavelId ? ' has' : ''}`}
                title={
                  item.responsavelId
                    ? `Responsável: ${membros.find((m) => m.id === item.responsavelId)?.nome}`
                    : 'Atribuir responsável'
                }
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setPicker({ item, x: r.left, y: r.bottom + 6 });
                }}
              >
                {item.responsavelId ? (
                  <Avatar
                    className="av"
                    nome={membros.find((m) => m.id === item.responsavelId)?.nome ?? '?'}
                    cor={membros.find((m) => m.id === item.responsavelId)?.cor}
                    url={membros.find((m) => m.id === item.responsavelId)?.avatar}
                  />
                ) : (
                  '+'
                )}
              </button>

              <button className="mdel" title="Excluir" onClick={() => excluirItem.mutate(item.id)}>
                ×
              </button>
            </div>
          ))
        )}
      </div>

      <div className="mfoot">
        <div className="addrow">
          <input
            value={novoItem}
            onChange={(e) => setNovoItem(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && adicionarItem()}
            placeholder="Adicionar item ao checklist..."
          />
          <button className="addbtn" onClick={adicionarItem}>
            Adicionar
          </button>
        </div>
        <div className="mhint">Enter para adicionar · clique no texto para editar · Esc fecha</div>
      </div>

      {picker && (
        <div
          className="picker"
          style={{ left: Math.min(picker.x, window.innerWidth - 236), top: picker.y }}
          onMouseLeave={() => setPicker(null)}
        >
          <div
            className={`pi${picker.item.responsavelId == null ? ' on' : ''}`}
            onClick={() => {
              editarItem.mutate({ id: picker.item.id, removerResponsavel: true });
              setPicker(null);
            }}
          >
            — sem responsável
          </div>
          {membros.map((m) => (
            <div
              key={m.id}
              className={`pi${picker.item.responsavelId === m.id ? ' on' : ''}`}
              onClick={() => {
                editarItem.mutate({ id: picker.item.id, responsavelId: m.id });
                setPicker(null);
              }}
            >
              <Avatar className="av" nome={m.nome} cor={m.cor} url={m.avatar} />
              <span>{m.nome}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** Quantos itens o checklist tem, para quem só precisa do número da aba. */
export function useResumoDoChecklist(tarefaId: number) {
  const { data } = useQuery<Checklist>({
    queryKey: chaveChecklist(tarefaId),
    queryFn: () => api(`/api/tarefas/${tarefaId}/checklist`),
  });
  const itens = data?.itens ?? [];
  return { total: itens.length, feitos: itens.filter((i) => i.feito).length };
}

function EditorDeTexto({
  valorInicial,
  aoConfirmar,
}: {
  valorInicial: string;
  aoConfirmar(texto: string | null): void;
}) {
  const [valor, setValor] = useState(valorInicial);
  const campo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    campo.current?.focus();
    campo.current?.select();
  }, []);

  return (
    <input
      ref={campo}
      className="edit"
      value={valor}
      onChange={(e) => setValor(e.target.value)}
      onBlur={() => aoConfirmar(valor.trim() || null)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') aoConfirmar(valor.trim() || null);
        if (e.key === 'Escape') aoConfirmar(null);
      }}
    />
  );
}

